import React, { useState, useEffect } from "react";
import { Card, Table, Empty, Badge, Tooltip, Tag, Button, Spin, message } from "antd";
import {
  CalendarOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import useBulkPrintPreparation from "../../hooks/useBulkPrintPreparation";
import { IMAGE_URL } from "../../constants";
import { colorStatusInfraction, statusText } from "../../utils/constants";
import { getInfraction } from "../../api/infractions/infractionRequest";
import PermissionRender from "../../components/PermissionRender";
import InfractionScreenshotCard from "../../components/InfractionDetail/InfractionScreenshotCard";
import BulkViolationPdf from "../../components/InfractionDetail/BulkViolationPdf";

const VehicleInfractionsTab = ({ infractions }) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isBulkPdfVisible, setIsBulkPdfVisible] = useState(false);
  const {
    items: bulkInfractions, captureRecord, captureRef,
    isPreparing: isCapturing, progress: captureProgress, prepare, reset,
  } = useBulkPrintPreparation(getInfraction);

  useEffect(() => {
    setSelectedRowKeys([]);
    setIsBulkPdfVisible(false);
    reset();
  }, [infractions, reset]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedKeys) => setSelectedRowKeys(newSelectedKeys),
  };

  const handleBulkPrint = async () => {
    if (selectedRowKeys.length === 0) return;

    try {
      if (await prepare([...selectedRowKeys])) setIsBulkPdfVisible(true);
    } catch (error) {
      console.error("Error preparing bulk print:", error);
      message.error("Erreur lors de la préparation de l'impression");
    }
  };
  // Fonction pour formater l'affichage des photos
  const renderPhotos = (photos) => {
    if (!photos) return <Empty description="Aucune photo" />;

    const photoArray = photos.split(",");
    return (
      <div className="flex flex-wrap gap-2">
        {photoArray.map((photo, index) => (
          <img
            key={index}
            src={`${IMAGE_URL}${photo}`}
            alt={`Photo ${index + 1}`}
            className="rounded-md object-cover w-24 h-24"
          />
        ))}
      </div>
    );
  };

  const goToInfractionPage = (id) => {
    window.open(`/infraction/${id}`, "_blank");
  };

  // Colonnes pour la table des infractions
  const infractionsColumns = [
    {
      title: "Référence",
      dataIndex: "reference",
      key: "reference",
      render: (text, record) => (
        <Tag
          color="purple"
          className="px-3 py-1 text-sm font-medium cursor-pointer"
          onClick={() => goToInfractionPage(record.id)}
        >
          {text}
        </Tag>
      ),
    },
    {
      title: "Date",
      dataIndex: "datetime_infraction",
      key: "datetime_infraction",
      render: (text) => (
        <div className="flex items-center">
          <CalendarOutlined className="mr-2 text-gray-500" />
          <span>{dayjs(text).format("LLL")}</span>
        </div>
      ),
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      render: (text) => (
        <div className="flex items-center">
          <DollarOutlined className="mr-2 text-green-600" />
          <span className="font-semibold">{text} MAD</span>
        </div>
      ),
    },
    {
      title: "Localisation",
      dataIndex: "location",
      key: "location",
      render: (text, record) => (
        <Tooltip title={`${record.latitude}, ${record.longitude}`}>
          <div className="flex items-center max-w-xs truncate">
            <EnvironmentOutlined className="mr-2 text-red-500" />
            <span>{text}</span>
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Badge
          className="badge-custom"
          status={colorStatusInfraction[status] || "default"}
          text={<span className="font-medium">{statusText(status)}</span>}
        />
      ),
    },
    {
      title: "Photos",
      dataIndex: "photos",
      key: "photos",
      render: renderPhotos,
    },
  ];

  return (
    <div className="p-4">
      <Spin spinning={isCapturing} tip={captureProgress}>
        <Card
          className="shadow-md hover:shadow-lg transition-all duration-300"
          bordered={false}
          title={
            <div className="flex items-center justify-between">
              <span className="text-lg font-medium">Infractions</span>
              <div className="flex items-center gap-2">
                <Button size="small" disabled={!infractions?.length || isCapturing} onClick={() => setSelectedRowKeys(infractions.map((item) => item.id))}>
                  Tout sélectionner
                </Button>
                <Button size="small" disabled={!selectedRowKeys.length || isCapturing} onClick={() => setSelectedRowKeys([])}>
                  Effacer la sélection
                </Button>
                <PermissionRender
                  requiredPermission={["infractions", "print_violation"]}
                >
                  <Button
                    type="primary"
                    icon={<PrinterOutlined />}
                    onClick={handleBulkPrint}
                    disabled={selectedRowKeys.length === 0}
                    loading={isCapturing}
                    className="bg-purple-500 hover:bg-purple-600 disabled:text-white"
                    size="small"
                  >
                    Imprimer ({selectedRowKeys.length})
                  </Button>
                </PermissionRender>
                <Tag color="red" className="px-3 py-1">
                  {infractions?.length || 0} infractions
                </Tag>
              </div>
            </div>
          }
        >
          {infractions && infractions.length > 0 ? (
            <Table
              rowSelection={rowSelection}
              dataSource={infractions}
              columns={infractionsColumns}
              rowKey="id"
              pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ["5", "10", "20", "50", "100"] }}
              className="border border-gray-200 rounded-lg"
              expandable={{
                expandedRowRender: (record) => (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-700">
                          <strong>Description:</strong>{" "}
                          {record.description || "Aucune description"}
                        </p>
                        <p className="text-gray-700">
                          <strong>Code QR:</strong> {record.qr_code}
                        </p>
                        <p className="text-gray-700">
                          <strong>Avertissement lié:</strong>{" "}
                          {record.warning_id
                            ? `#${record.warning?.reference || record.warning_id}`
                            : "Aucun"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-700">
                          <strong>Type d'infraction:</strong>{" "}
                          {record.type_infraction?.name || "—"}
                        </p>
                        <p className="text-gray-700">
                          <strong>Contrat:</strong> {record.zone?.name || "—"}
                        </p>
                        <p className="text-gray-700">
                          <strong>Zone:</strong> {record.section?.name || "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                ),
              }}
            />
          ) : (
            <Empty
              description="Aucune infraction pour ce véhicule"
              className="my-8"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Card>
      </Spin>

      {isCapturing && (
        <div className="mt-4">
          <Button onClick={reset}>Annuler la préparation</Button>
        </div>
      )}
      {/* Only one card is mounted during screenshot capture. */}
      {captureRecord && (
        <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
          <InfractionScreenshotCard ref={captureRef} infraction={captureRecord} />
        </div>
      )}

      <BulkViolationPdf
        visible={isBulkPdfVisible}
        onClose={() => {
          setIsBulkPdfVisible(false);
          reset();
        }}
        infractions={bulkInfractions}
      />
    </div>
  );
};

export default VehicleInfractionsTab;
